import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { ControllerProps, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { OtpFormField } from '~components/FormFields/OtpFormField';
import useLogin from '~hooks/useLogin/useLogin';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import FormWrapperBox from './FormWrapperBox';
import { SIGN_IN_OTP_LENGTH, signInWithOtpValidationSchema } from './validationSchemas';

interface SignInWithOtpFormProps {
  onBack: () => void;
  phone: string;
  phoneId: string;
}

interface SignInWithOtpDataProps extends yup.InferType<
  typeof signInWithOtpValidationSchema
> {}

const SignInWithOtpForm = ({ onBack, phone, phoneId }: SignInWithOtpFormProps) => {
  const { toasterStore } = useStores();
  const [isLoading, setIsLoading] = useState(false);
  const { loginWithOtp, sendOtpSms } = useLogin();

  const handleResendButtonClick = useCallback(async () => {
    setIsLoading(true);
    try {
      await sendOtpSms({ phone });
      toasterStore.push(alert(t('actions.new_otp_sms_sent'), AlertTypes.success));
    } catch {
      // Errors are handled in useLogin.
    } finally {
      setIsLoading(false);
    }
  }, [phone, sendOtpSms, toasterStore]);

  const onSubmit = async (data: SignInWithOtpDataProps) => {
    setIsLoading(true);
    try {
      await loginWithOtp({ phone_id: phoneId, code: data.otp });
    } catch {
      // Errors are handled in useLogin.
    } finally {
      setIsLoading(false);
    }
  };

  const { handleSubmit, control } = useForm<SignInWithOtpDataProps>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(signInWithOtpValidationSchema),
  });

  return (
    <FormWrapperBox hideLogo onBack={onBack} sx={{ pt: 10 }}>
      <Typography
        variant="body1"
        sx={{
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        {t('actions.enter_verification_code')}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mt: 1,
          textAlign: 'center',
        }}
      >
        {t('actions.input_code_sent')}
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          mt: 5,
        }}
      >
        <OtpFormField
          length={SIGN_IN_OTP_LENGTH}
          otpType="numeric"
          control={control as unknown as ControllerProps['control']}
          name="otp"
        />

        <Box
          sx={{
            width: '100%',
          }}
        >
          <Button
            fullWidth
            color="primary"
            variant="contained"
            type="submit"
            loading={isLoading}
          >
            {t('actions.signIn')}
          </Button>

          <Button
            sx={{ mt: 1 }}
            fullWidth
            size="large"
            color="inherit"
            variant="text"
            onClick={handleResendButtonClick}
            loading={isLoading}
            type="button"
          >
            {t('actions.resend_code')}
          </Button>
        </Box>
      </Box>
    </FormWrapperBox>
  );
};

export default SignInWithOtpForm;
